/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslator, useAppContext } from '@front-activerse/common-components';
import { Helmet } from 'react-helmet';
import { Col, Container, Row } from 'reactstrap';
import ExternalProviderContainer from '../../Auth/ExternalProvider/ExternalProviderContainer';
import { RETURN_URL_LOGIN } from '../../Auth/ExternalProvider/ReturnUrls';
import AuthLoginByEmailAndPasswordForm from './AuthLoginByEmailAndPasswordForm';
import Layout from '../../Layout/Layout';

import { currencySymbols } from '../../CheckoutMDS/const';

const AuthLoginByEmailAndPasswordContainer = ({
  login,
  isLoggedIn,
  userDataOnDemand,
  redirectToPathAfterSuccess,
  displayAppleButton,
  displayFacebookButton,
  displayGoogleButton,
  registerButtonURL,
  action,
  productVariant,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslator();

  const {
    consts: { APP_BRAND },
  } = useAppContext();

  useEffect(() => {
    if (isLoggedIn) {
      navigate(redirectToPathAfterSuccess);
    }
  }, [isLoggedIn, navigate, redirectToPathAfterSuccess]);

  let price = productVariant?.saleProduct?.price?.regular?.amount || null;
  let currency = productVariant?.saleProduct?.price?.regular?.currency || '';

  if (productVariant?.saleProduct?.price?.promo?.amount) {
    price = productVariant?.saleProduct?.price?.promo?.amount;
    currency = productVariant?.saleProduct?.price?.promo?.currency;
  }

  const renderPaymentMethods = () => {
    const appBrandWithApendix = APP_BRAND === 'dtba' ? 'dl-space.fra1' : `${APP_BRAND}.sfo2`;

    const isMacLike = /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent);
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isMobileChrome = isChrome && /Mobile/.test(navigator.userAgent);
    const shouldShowApplePay = isMacLike && (isSafari || isMobileChrome);

    if (APP_BRAND === 'dtba') {
      return (
        <>
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/Stripe.svg`}
            alt="Stripe"
          />
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/CreditCards_Visa_MasterCard_Maestro.svg`}
            alt="Credit Cards"
          />
          {shouldShowApplePay && (
            <img
              className="me-m mb-m"
              src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/ApplePay.svg`}
              alt="Apple Pay"
            />
          )}
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/GooglePay.svg`}
            alt="Google Pay"
          />
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/Link.svg`}
            alt="Link"
          />
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/Klarna.svg`}
            alt="Klarna"
          />
        </>
      );
    }

    return (
      <>
        <img
          className="me-m mb-m"
          src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/Stripe.svg`}
          alt="Stripe"
        />
        <img
          className="me-m mb-m"
          src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/CreditCards_Visa_MasterCard_AmericanExpress.svg`}
          alt="American Express"
        />
        {shouldShowApplePay && (
          <img
            className="me-m mb-m"
            src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/ApplePay.svg`}
            alt="Apple Pay"
          />
        )}
        <img
          className="me-m mb-m"
          src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/GooglePay.svg`}
          alt="Google Pay"
        />
        <img
          className="me-m mb-m"
          src={`https://${appBrandWithApendix}.cdn.digitaloceanspaces.com/web/cart/Link.svg`}
          alt="Link"
        />
      </>
    );
  };

  return (
    <Layout simpleLayout>
      {!isLoggedIn && (
        <>
          <Helmet>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
          <div className="page-template-auth full-height7">
            <Container className="py-xxl">
              <h1 className="text-center title-medium semi-bold mb-xl">{t('yourCart.title')}</h1>
              <Row>
                <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                  <div className="page-container">
                    <div className="auth-container">
                      <h1 className="text-center title-medium semi-bold mb-xl">
                        {t('logIn.title')}
                      </h1>
                      <ExternalProviderContainer
                        returnUrl={RETURN_URL_LOGIN}
                        displayAppleButton={displayAppleButton}
                        displayFacebookButton={displayFacebookButton}
                        displayGoogleButton={displayGoogleButton}
                        fullWidth
                        action={action}
                      />

                      {(displayAppleButton || displayFacebookButton || displayGoogleButton) && (
                        <div className="line">
                          <span>{t('authorization.continueWithEmail.description')}</span>
                        </div>
                      )}

                      <AuthLoginByEmailAndPasswordForm
                        login={login}
                        userDataOnDemand={userDataOnDemand}
                        redirectToPathAfterSuccess={redirectToPathAfterSuccess}
                      />
                    </div>
                  </div>
                  {registerButtonURL && (
                    <p className="mt-xl text-center">
                      {t('authorization.dontHaveAnAccount')}{' '}
                      <Link to={registerButtonURL}>{t('authorization.signUp.button')}</Link>
                    </p>
                  )}
                </Col>
                <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
                  <div className="page-container">
                    <div className="auth-container">
                      <h3 className="title-medium semi-bold mb-xl">
                        {t('paymentMethods.summary.title')}
                      </h3>
                      <div>
                        {productVariant ? (
                          <div className="d-flex justify-content-between">
                            <p className="mb-0 pe-4">{productVariant.saleProduct.name}</p>
                            <p className="mb-0 text-nowrap">
                              {price} {currencySymbols[currency]}
                            </p>
                          </div>
                        ) : (
                          <div className="d-flex justify-content-between">
                            <p className="placeholder mb-0">Product name</p>
                            <p className="placeholder mb-0">Price</p>
                          </div>
                        )}

                        <hr />
                        <div className="d-flex justify-content-between">
                          <h3 className="title-medium semi-bold mb-0">
                            {t('paymentMethods.summary.total.title')}
                          </h3>
                          {productVariant ? (
                            <h3 className="title-medium semi-bold mb-0">
                              {price} {currencySymbols[currency]}
                            </h3>
                          ) : (
                            <h3 className="title-medium semi-bold placeholder mb-0">Price</h3>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-3 mb-xl">
                    <p className="semi-bold mt-xl">{t('acceptedPaymentMethods.title')}</p>
                    {renderPaymentMethods()}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </>
      )}
    </Layout>
  );
};

AuthLoginByEmailAndPasswordContainer.propTypes = {
  login: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  userDataOnDemand: PropTypes.func.isRequired,
  redirectToPathAfterSuccess: PropTypes.string.isRequired,
};

export default AuthLoginByEmailAndPasswordContainer;
